import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Trash2, MessageSquare } from "lucide-react";
import { StarRating, StarInput } from "./StarRating";
import { toast } from "sonner";

/**
 * Reviews block for any target (product / doctor).
 * Shows summary, list of published reviews, and the post/edit form for signed-in users.
 */
export default function ReviewsBlock({ targetType, targetId, label = "Reviews" }) {
    const { user } = useAuth();
    const [summary, setSummary] = useState({ count: 0, average: 0, breakdown: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 } });
    const [reviews, setReviews] = useState([]);
    const [myReview, setMyReview] = useState(null);
    const [rating, setRating] = useState(0);
    const [title, setTitle] = useState("");
    const [comment, setComment] = useState("");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const [s, r] = await Promise.all([
                api.get("/reviews/summary", { params: { target_type: targetType, target_id: targetId } }),
                api.get("/reviews", { params: { target_type: targetType, target_id: targetId } }),
            ]);
            setSummary(s.data);
            setReviews(r.data.rows || []);
        } catch { /* surfaced */ }
    };

    useEffect(() => {
        if (!targetId) return;
        load();
        if (user) {
            api.get("/reviews/mine").then(r => {
                const mine = (r.data || []).find(x => x.target_type === targetType && x.target_id === targetId);
                if (mine) {
                    setMyReview(mine);
                    setRating(mine.rating);
                    setTitle(mine.title || "");
                    setComment(mine.comment || "");
                }
            }).catch(() => { });
        }
        // eslint-disable-next-line
    }, [targetType, targetId, user?.id]);

    const submit = async (e) => {
        e.preventDefault();
        if (!rating) return toast.error("Please tap a star to rate first");
        if (!comment.trim() || comment.trim().length < 2) return toast.error("Tell us a bit more — at least 2 characters");
        setBusy(true);
        try {
            const { data } = await api.post("/reviews", {
                target_type: targetType,
                target_id: targetId,
                rating,
                title: title.trim(),
                comment: comment.trim(),
            });
            setMyReview(data);
            toast.success(myReview ? "Review updated" : "Thanks for your review!");
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't post review");
        } finally { setBusy(false); }
    };

    const remove = async () => {
        if (!myReview) return;
        if (!window.confirm("Delete your review?")) return;
        try {
            await api.delete(`/reviews/${myReview.id}`);
            setMyReview(null);
            setRating(0); setTitle(""); setComment("");
            toast.success("Review removed");
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Delete failed");
        }
    };

    const total = summary.count || 1;
    return (
        <section className="border-t border-stone-200 mt-10 pt-8" data-testid={`reviews-block-${targetType}-${targetId}`}>
            <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
                <div>
                    <div className="label-eyebrow text-sky-700">{label}</div>
                    <h3 className="font-serif text-2xl text-stone-900 mt-1 flex items-center gap-3">
                        Reviews{" "}
                        {summary.count > 0 && (
                            <StarRating value={summary.average} count={summary.count} size={18} showValue />
                        )}
                    </h3>
                </div>
            </div>

            {summary.count > 0 && (
                <div className="bg-stone-50 border border-stone-200 p-4 mb-6 max-w-md">
                    {[5, 4, 3, 2, 1].map((n) => {
                        const pct = Math.round(((summary.breakdown[n] || 0) / total) * 100);
                        return (
                            <div key={n} className="flex items-center gap-2 text-xs">
                                <span className="w-8 text-stone-600">{n}★</span>
                                <div className="flex-1 h-2 bg-stone-200 overflow-hidden">
                                    <div className="h-full bg-amber-400" style={{ width: `${pct}%` }} />
                                </div>
                                <span className="w-10 text-right text-stone-500">{summary.breakdown[n] || 0}</span>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Post / edit form */}
            {user ? (
                <form onSubmit={submit} className="bg-white border border-stone-200 p-5 mb-8 space-y-4" data-testid="review-form">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                            <div className="text-sm font-semibold text-stone-900">{myReview ? "Update your review" : "Share your experience"}</div>
                            <div className="text-xs text-stone-500">Posting as <strong>{user.name || user.email}</strong></div>
                        </div>
                        {myReview && (
                            <button type="button" onClick={remove} className="text-xs text-red-700 hover:underline inline-flex items-center gap-1" data-testid="review-delete">
                                <Trash2 className="w-3 h-3" />Delete my review
                            </button>
                        )}
                    </div>
                    <StarInput value={rating} onChange={setRating} dataTestIdBase="review-star" />
                    <Input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        maxLength={100}
                        placeholder="A short headline (optional)"
                        className="rounded-none border-stone-300"
                        data-testid="review-title"
                    />
                    <Textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={4}
                        maxLength={2000}
                        placeholder={targetType === "doctor" ? "How was your consultation? Did the remedy help?" : "How effective was this medicine? Any side notes for fellow customers?"}
                        className="rounded-none border-stone-300"
                        data-testid="review-comment"
                    />
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <span className="text-[11px] text-stone-500">{comment.length}/2000 characters</span>
                        <Button type="submit" disabled={busy} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="review-submit">
                            {busy ? "Posting…" : myReview ? "Update review" : "Post review"}
                        </Button>
                    </div>
                </form>
            ) : (
                <div className="bg-stone-50 border border-stone-200 p-4 mb-8 text-sm text-stone-700 flex flex-wrap items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-sky-700" />
                    <span>Sign in to rate and review.</span>
                    <Link to="/login" className="text-sky-700 hover:underline font-medium ml-1">Sign in</Link>
                </div>
            )}

            {/* Reviews list */}
            {reviews.length === 0 ? (
                <div className="text-sm text-stone-500 py-6 text-center border border-stone-200">No reviews yet — be the first!</div>
            ) : (
                <ul className="space-y-4">
                    {reviews.map((r) => (
                        <li key={r.id} className="border border-stone-200 bg-white p-4" data-testid={`review-${r.id}`}>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div>
                                    <div className="font-semibold text-stone-900 text-sm flex items-center gap-2 flex-wrap">
                                        {r.user_name || "Anonymous"}
                                        {r.is_verified && (
                                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`review-verified-${r.id}`} title={targetType === "doctor" ? "Verified consultation" : "Verified buyer"}>
                                                <svg viewBox="0 0 24 24" width="10" height="10" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" /></svg>
                                                {targetType === "doctor" ? "Verified patient" : "Verified buyer"}
                                            </span>
                                        )}
                                    </div>
                                    <StarRating value={r.rating} size={12} className="mt-1" />
                                </div>
                                <div className="text-[11px] text-stone-500">{(r.created_at || "").slice(0, 10)}</div>
                            </div>
                            {r.title && <div className="font-medium text-stone-900 mt-2">{r.title}</div>}
                            <p className="text-sm text-stone-700 mt-1 whitespace-pre-line">{r.comment}</p>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}
